import React from "react";
import { DateRangeOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { auditDashboardTheme } from "../../theme";
import type { AuditListCardData } from "../../types/auditList";
import AuditSummaryComparisonTable from "../shared/AuditSummaryComparisonTable";

interface RangeSummaryCardProps {
  card: AuditListCardData;
  active?: boolean;
  onSelect?: (reportDate: string) => void;
}

export default function RangeSummaryCard({ card, active = false, onSelect }: RangeSummaryCardProps) {
  const colors = auditDashboardTheme.colors;

  const handleClick = () => {
    onSelect?.(card.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onSelect) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(card.id);
    }
  };

  return (
    <Box
      component="div"
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-pressed={onSelect ? active : undefined}
      onClick={onSelect ? handleClick : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      sx={{
        appearance: "none",
        position: "relative",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        width: "calc(100% - 12px)",
        m: "6px",
        border: "1px solid",
        borderColor: active ? colors.blue : colors.containerBorder,
        borderRadius: "6px",
        p: 0,
        color: "inherit",
        bgcolor: colors.surface,
        textAlign: "left",
        cursor: onSelect ? "pointer" : "default",
        font: "inherit",
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        boxShadow: active ? "0 0 0 2px rgba(44, 108, 160, 0.16)" : "none",
        boxSizing: "border-box",
        overflow: "hidden",
        userSelect: "text",
        "&:hover": {
          borderColor: colors.blue,
        },
        "&:focus-visible": {
          outline: "2px solid rgba(44, 108, 160, 0.28)",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          minWidth: 0,
          gap: "10px",
          px: "14px",
          py: "13px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.16)",
          bgcolor: colors.blue,
          color: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            minWidth: 0,
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: "12px",
            rowGap: "4px",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <DateRangeOutlined aria-hidden sx={{ flexShrink: 0, fontSize: 19, color: "rgba(255, 255, 255, 0.82)" }} />
            <Typography
              component="h3"
              sx={{
                m: 0,
                color: "inherit",
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              {card.title}
            </Typography>
          </Box>
          {card.rangeDayCount != null ? (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: "8px",
                py: "3px",
                borderRadius: "999px",
                bgcolor: "rgba(255, 255, 255, 0.14)",
                color: "#FFFFFF",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {card.rangeDayCount.toLocaleString("en-US")} {card.rangeDayCount === 1 ? "day" : "days"}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            minWidth: 0,
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: "12px",
            rowGap: "4px",
            flexWrap: "wrap",
          }}
        >
          {card.subtitle ? (
            <Typography
              component="span"
              sx={{
                minWidth: 0,
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {card.subtitle}
            </Typography>
          ) : null}
          {card.auditCount != null ? (
            <Typography
              component="span"
              sx={{
                flexShrink: 0,
                maxWidth: "100%",
                ml: "auto",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {card.auditCount.toLocaleString("en-US")} {card.auditCount === 1 ? "audit" : "audits"}
              {card.averageAuditsPerDay != null
                ? ` \u00b7 ${card.averageAuditsPerDay.toLocaleString("en-US", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}/day`
                : ""}
            </Typography>
          ) : null}
        </Box>

        {card.headerVarianceLabel ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: "12px",
              pt: "11px",
              borderTop: "1px solid rgba(255, 255, 255, 0.16)",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              Net {card.headerVarianceLabel}
            </Typography>
            {card.headerVarianceValue ? (
              <Typography
                component="span"
                sx={{
                  color: "#FFFFFF",
                  fontSize: 17,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {card.headerVarianceValue}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ p: "12px", bgcolor: colors.surface }}>
        <AuditSummaryComparisonTable rows={card.rows} variant="range" />
      </Box>
    </Box>
  );
}
